import 'shared-react/lumen/styles.css';

import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import GeneralPageMain from 'lumen/components/general/page/main';
import SettingsDrawerNavigationHeader from 'lumen/components/settings/drawer/navigation-header';
import type MarketSettingsController from 'lumen/controllers/market/settings';
import hasFeature from 'lumen/helpers/has-feature';
import hasFeatureToggle from 'lumen/helpers/has-feature-toggle';
import mountReact from 'lumen/modifiers/mount-react';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { MarketSettingsSidePanel } from 'shared-react/lumen';

interface MarketSettingsSignature {
  Args: {
    controller: MarketSettingsController;
    model: unknown;
  };
}

<template>
  <GeneralPageMain class="navbar-settings">
    {{#if (hasFeatureToggle FEATURE_TOGGLES.REACT_SETTINGS_SIDE_PANEL)}}
      <div class="flex" {{mountReact MarketSettingsSidePanel}}></div>
    {{else}}
      <div
        class="settings-drawer
          {{if
            @controller.isSettingsDrawerOpen
            'settings-drawer--open'
            'settings-drawer--close'
          }}
          "
      >
        <SettingsDrawerNavigationHeader
          @isDrawerOpen={{@controller.isSettingsDrawerOpen}}
          @toggleNavbar={{@controller.toggleNavbar}}
        />
        {{#if @controller.isSettingsDrawerOpen}}
          <ul class="text-300--medium settings-drawer__content flex flex-col">
            {{#if @controller.hasPersonalSettingsGroup}}
              <li class="settings-drawer__group">
                <h4
                  class="settings-drawer__group-title"
                  data-test-id="settings-group-personal"
                >
                  {{t "settings.section.personal_settings"}}
                </h4>
                <ul class="settings-drawer__list">
                  {{#if @controller.access.hasPersonalSettingsTab}}
                    <li>
                      <LinkTo
                        @route="market.settings.personal"
                        class="settings-drawer__link"
                        data-test-id="account-settings"
                      >
                        {{t "menu.my_account"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.access.canEditPersonalSettingsPassword}}
                    <li>
                      <LinkTo
                        @route="market.settings.security"
                        class="settings-drawer__link"
                      >
                        {{t "settings.security.title"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.theme.hasPermissions}}
                    <li>
                      <LinkTo
                        @route="market.settings.theme"
                        class="settings-drawer__link"
                        data-test-id="settings-item-theme"
                      >
                        {{t "settings.theme.title"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                </ul>
              </li>
            {{/if}}

            {{#if @controller.hasGeneralGroup}}
              <li class="settings-drawer__group">
                <h4
                  class="settings-drawer__group-title"
                  data-test-id="settings-group-general"
                >
                  {{t "settings.general"}}
                </h4>
                <ul class="settings-drawer__list">
                  {{#if (hasFeature "settings.hotel_access.edit")}}
                    <li>
                      <LinkTo
                        @route="market.settings.hotels"
                        class="settings-drawer__link"
                        data-test-id="link-hotel-access"
                      >
                        {{t "settings.section.hotel_access"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.hasCentralMapping}}
                    <li>
                      <LinkTo
                        @current-when={{@controller.isCentralMappingTabActive}}
                        @route="market.settings.central-mapping.menu"
                        class="settings-drawer__link flex flex-row items-center justify-between"
                        {{on "click" @controller.onClickCentralMappingTab}}
                      >
                        {{t "settings.central-mapping.mapping"}}
                      </LinkTo>

                    </li>
                  {{/if}}
                  {{#if (hasFeature "reports.view.settings")}}
                    <li>
                      <LinkTo
                        @route="market.settings.reports"
                        class="settings-drawer__link"
                      >
                        {{t "settings.section.scheduled_reports"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                </ul>
              </li>
            {{/if}}

            {{#if @controller.hasBusinessIntelligenceGroup}}
              <li class="settings-drawer__group">
                <h4
                  class="settings-drawer__group-title"
                  data-test-id="settings-group-business-intelligence"
                >
                  {{t "settings.section.business_intelligence"}}
                </h4>

                <ul class="settings-drawer__list">
                  {{#if @controller.hasBusinessIntelligenceTab}}
                    <li>
                      <LinkTo
                        @route="market.settings.spider"
                        class="settings-drawer__link"
                        data-test-id="spider-settings"
                      >
                        {{t "settings.section.business_intelligence"}}
                        ({{t "settings.section.bi"}})
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.hasTransactionMapping}}
                    <li>
                      <LinkTo
                        @route="market.settings.transaction-mapping"
                        class="settings-drawer__link"
                        data-test-id="transaction-mapping-tab"
                      >
                        {{t "settings.section.transaction_mapping"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.hasPropertyTags}}
                    <li>
                      <LinkTo
                        @route="market.settings.property-tags"
                        class="settings-drawer__link"
                        data-test-id="property-tags-tab"
                      >
                        {{t "settings.property_tags.title"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.hasCurrencyConversion}}
                    <li>
                      <LinkTo
                        @route="market.settings.currency-conversion"
                        class="settings-drawer__link"
                        data-test-id="currency-conversion-tab"
                        {{on "click" @controller.onClickCurrencyConversionTab}}
                      >
                        {{t "general.currency"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                </ul>
              </li>
            {{/if}}
          </ul>
        {{/if}}
      </div>
    {{/if}}
    <div class="content-wrapper main-content flex flex-col gap-0">
      {{outlet}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<MarketSettingsSignature>;
